import JSZip from 'jszip';
import type { Presentation, Slide, SlideElement, ShapeType } from './types';

export class PptxParser {
    private slideWidth = 12192000;
    private slideHeight = 6858000;
    private readonly CANVAS_WIDTH = 1200;
    private readonly CANVAS_HEIGHT = 675;

    async parse(input: File | Blob | ArrayBuffer | string): Promise<Presentation> {
        let data: any = input;
        if (typeof input === 'string') {
            const response = await fetch(input);
            if (!response.ok) {
                const status = response.status;
                const statusText = response.statusText;

                let detail = '';
                try {
                    const errorJson = await response.json();
                    detail = errorJson.details || errorJson.error || '';
                } catch (e) {
                    // Ignore if not JSON
                }

                throw new Error(`Failed to fetch PPTX from ${input}: ${status} ${statusText} ${detail ? `(${detail})` : ''}`);
            }
            data = await response.arrayBuffer();
        }
        const zip = await JSZip.loadAsync(data);
        const presentationXml = await zip.file('ppt/presentation.xml')?.async('string');
        if (!presentationXml) throw new Error('Invalid PPTX');

        const parser = new DOMParser();
        const presentationDoc = parser.parseFromString(presentationXml, 'text/xml');

        const sldSz = this.findFirstByLocalName(presentationDoc, 'sldSz');
        if (sldSz) {
            this.slideWidth = parseInt(this.getAttr(sldSz, 'cx') || '12192000');
            this.slideHeight = parseInt(this.getAttr(sldSz, 'cy') || '6858000');
        }

        const sldIdList = Array.from(presentationDoc.getElementsByTagNameNS('*', 'sldId'));
        const slides: Slide[] = [];

        for (let i = 0; i < sldIdList.length; i++) {
            const slideNum = i + 1;
            const slidePath = `ppt/slides/slide${slideNum}.xml`;
            const slideXml = await zip.file(slidePath)?.async('string');
            if (slideXml) {
                const slide = await this.parseSlide(slideXml, slideNum, zip);
                slides.push(slide);
            }
        }

        return {
            slides,
            layout: { width: this.slideWidth, height: this.slideHeight }
        };
    }

    private getAttr(el: Element, name: string): string | null {
        return el.getAttribute(name) || el.getAttribute(`a:${name}`) || el.getAttribute(`p:${name}`) || el.getAttribute(`r:${name}`);
    }

    private findFirstByLocalName(parent: Document | Element, name: string): Element | null {
        const elements = parent.getElementsByTagNameNS('*', name);
        return elements.length > 0 ? elements[0] : null;
    }

    private findAllByLocalName(parent: Document | Element, name: string): Element[] {
        return Array.from(parent.getElementsByTagNameNS('*', name));
    }

    private scaleX(emu: number): number {
        return (emu / this.slideWidth) * this.CANVAS_WIDTH;
    }

    private scaleY(emu: number): number {
        return (emu / this.slideHeight) * this.CANVAS_HEIGHT;
    }

    private parseColor(el: Element): { color: string, opacity: number } {
        const srgbClr = this.findFirstByLocalName(el, 'srgbClr');
        if (srgbClr) {
            const hex = `#${this.getAttr(srgbClr, 'val')}`;
            const alphaNode = this.findFirstByLocalName(srgbClr, 'alpha');
            const opacity = alphaNode ? parseInt(this.getAttr(alphaNode, 'val') || '100000') / 100000 : 1;
            return { color: hex, opacity };
        }
        const schemeClr = this.findFirstByLocalName(el, 'schemeClr');
        if (schemeClr) {
            const alphaNode = this.findFirstByLocalName(schemeClr, 'alpha');
            const opacity = alphaNode ? parseInt(this.getAttr(alphaNode, 'val') || '100000') / 100000 : 1;
            return { color: '#000000', opacity }; // Themes usually default to dark if not specified
        }
        return { color: '#000000', opacity: 1 };
    }

    private async resolveImage(relId: string, slideIndex: number, zip: JSZip): Promise<Blob | null> {
        const relsXml = await zip.file(`ppt/slides/_rels/slide${slideIndex}.xml.rels`)?.async('string');
        if (!relsXml) return null;

        const parser = new DOMParser();
        const relsDoc = parser.parseFromString(relsXml, 'text/xml');
        const relationship = Array.from(relsDoc.getElementsByTagName('Relationship'))
            .find(r => r.getAttribute('Id') === relId);

        const target = relationship?.getAttribute('Target');
        if (target) {
            const mediaPath = (target.startsWith('../') ? `ppt/${target.substring(3)}` : `ppt/slides/${target}`).replace('ppt/slides/../', 'ppt/');
            return await zip.file(mediaPath)?.async('blob') || null;
        }
        return null;
    }

    private async parseSlide(xml: string, slideIndex: number, zip: JSZip): Promise<Slide> {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'text/xml');
        const elements: SlideElement[] = [];
        let zIndex = 0;

        // Check for Background Image
        const bg = this.findFirstByLocalName(doc, 'bg');
        if (bg) {
            const blip = this.findFirstByLocalName(bg, 'blip');
            const relId = blip?.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'embed') ||
                blip?.getAttribute('r:embed');
            if (relId) {
                const mediaFile = await this.resolveImage(relId, slideIndex, zip);
                if (mediaFile) {
                    elements.push({
                        id: `bg-${slideIndex}`,
                        type: 'image',
                        src: URL.createObjectURL(mediaFile),
                        x: 0,
                        y: 0,
                        width: this.CANVAS_WIDTH,
                        height: this.CANVAS_HEIGHT,
                        zIndex: zIndex++,
                        opacity: 1
                    });
                }
            }
        }

        const spTree = this.findFirstByLocalName(doc, 'spTree');
        if (!spTree) return { id: `slide-${slideIndex}`, elements };

        const children = Array.from(spTree.children);
        for (const child of children) {
            const localName = child.localName;

            if (localName === 'sp') {
                const txBody = this.findFirstByLocalName(child, 'txBody');
                const nvSpPr = this.findFirstByLocalName(child, 'nvSpPr');
                const nvPr = nvSpPr ? this.findFirstByLocalName(nvSpPr, 'nvPr') : null;
                const ph = nvPr ? this.findFirstByLocalName(nvPr, 'ph') : null;
                const phType = ph ? this.getAttr(ph, 'type') : null;
                const isBodyPlaceholder = ph && (!phType || phType === 'body');
                const xfrm = this.findFirstByLocalName(child, 'xfrm');
                const off = xfrm ? this.findFirstByLocalName(xfrm, 'off') : null;
                const ext = xfrm ? this.findFirstByLocalName(xfrm, 'ext') : null;

                if (txBody && off && ext) {
                    const paragraphs = this.findAllByLocalName(txBody, 'p');
                    let content = '';
                    let fontSize = 18;
                    let color = '#000000';
                    let opacity = 1;
                    let isBulleted = false;
                    let isNumbered = false;

                    for (const p of paragraphs) {
                        const pPr = this.findFirstByLocalName(p, 'pPr');
                        const buNone = pPr ? this.findFirstByLocalName(pPr, 'buNone') : null;
                        let paragraphContent = '';

                        // Parse paragraph properties
                        if (pPr && !buNone) {
                            if (this.findFirstByLocalName(pPr, 'buAutoNum')) {
                                isNumbered = true;
                            } else if (this.findFirstByLocalName(pPr, 'buChar')) {
                                isBulleted = true;
                            } else {
                                // Heuristic 1: Hanging indent
                                const indent = parseInt(this.getAttr(pPr, 'indent') || '0');
                                if (indent < 0) {
                                    isBulleted = true;
                                }
                                // Heuristic 2: Body placeholder inheritance
                                else if (isBodyPlaceholder) {
                                    isBulleted = true;
                                }
                            }
                        } else if (!pPr && isBodyPlaceholder) {
                            isBulleted = true;
                        }

                        const runs = this.findAllByLocalName(p, 'r');
                        for (const r of runs) {
                            const t = this.findFirstByLocalName(r, 't');
                            if (t) paragraphContent += t.textContent;

                            const rPr = this.findFirstByLocalName(r, 'rPr');
                            if (rPr) {
                                const sz = this.getAttr(rPr, 'sz');
                                if (sz) fontSize = (parseInt(sz) / 100) * 2;
                                const style = this.parseColor(rPr);
                                color = style.color;
                                opacity = style.opacity;
                            }
                        }

                        // Heuristic 3: Baked-in text bullets
                        // Check if text starts with a bullet character (•, etc)
                        const bulletRegex = /^\s*([•\u2022\u2023\u25E6\u2043\u2219])\s+/;
                        const match = paragraphContent.match(bulletRegex);
                        if (match) {
                            isBulleted = true;
                            // Remove the bullet char and trim leading whitespace
                            paragraphContent = paragraphContent.replace(bulletRegex, '');
                        }

                        content += paragraphContent + '\n';
                    }

                    if (content.trim()) {
                        elements.push({
                            id: `el-${slideIndex}-${Date.now()}-${zIndex}`,
                            type: 'text',
                            content: content.trim(),
                            x: this.scaleX(parseInt(this.getAttr(off, 'x') || '0')),
                            y: this.scaleY(parseInt(this.getAttr(off, 'y') || '0')),
                            width: this.scaleX(parseInt(this.getAttr(ext, 'cx') || '0')),
                            height: this.scaleY(parseInt(this.getAttr(ext, 'cy') || '0')),
                            fontSize,
                            color,
                            fontFamily: 'Arial',
                            zIndex: zIndex++,
                            isBulleted,
                            isNumbered,
                            listType: isNumbered ? 'number-decimal' : (isBulleted ? 'bullet-disc' : 'none'),
                            opacity
                        });
                        continue;
                    }
                }

                const prstGeom = this.findFirstByLocalName(child, 'prstGeom');
                if (prstGeom && off && ext) {
                    const prst = this.getAttr(prstGeom, 'prst') || 'rect';
                    const spPr = this.findFirstByLocalName(child, 'spPr');
                    const style = spPr ? this.parseColor(spPr) : { color: '#cccccc', opacity: 1 };

                    // Mapping PPTX preset names to our internal shape types
                    const mapping: Record<string, ShapeType> = {
                        'rect': 'rect',
                        'roundRect': 'rect',
                        'ellipse': 'ellipse',
                        'circle': 'ellipse',
                        'triangle': 'triangle',
                        'rtTriangle': 'rightTriangle',
                        'diamond': 'rhombus',
                        'parallelogram': 'parallelogram',
                        'trapezoid': 'trapezoid',
                        'pentagon': 'pentagon',
                        'hexagon': 'hexagon',
                        'heptagon': 'heptagon',
                        'octagon': 'octagon',
                        'heart': 'heart',
                        'smileyFace': 'smiley',
                        'sun': 'sun',
                        'moon': 'moon',
                        'rightArrow': 'arrowRight',
                        'leftArrow': 'arrowLeft',
                        'upArrow': 'arrowUp',
                        'downArrow': 'arrowDown',
                        'leftRightArrow': 'arrowLeftRight',
                        'upDownArrow': 'arrowUpDown',
                        'star4': 'star4',
                        'star5': 'star5',
                        'star6': 'star6',
                        'star8': 'star8',
                        'cloud': 'cloud',
                        'lightningBolt': 'lightning',
                        'plus': 'plus',
                        'minus': 'minus',
                        'multiply': 'multiply',
                        'divide': 'divide',
                        'equal': 'equal'
                    };

                    elements.push({
                        id: `el-${slideIndex}-${Date.now()}-${zIndex}`,
                        type: 'shape',
                        shapeType: (mapping[prst] || 'rect'),
                        fill: style.color,
                        opacity: style.opacity,
                        x: this.scaleX(parseInt(this.getAttr(off, 'x') || '0')),
                        y: this.scaleY(parseInt(this.getAttr(off, 'y') || '0')),
                        width: this.scaleX(parseInt(this.getAttr(ext, 'cx') || '0')),
                        height: this.scaleY(parseInt(this.getAttr(ext, 'cy') || '0')),
                        zIndex: zIndex++
                    });
                }
            }

            if (localName === 'pic') {
                const blip = this.findFirstByLocalName(child, 'blip');
                const relId = blip?.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'embed') ||
                    blip?.getAttribute('r:embed');

                const xfrm = this.findFirstByLocalName(child, 'xfrm');
                const off = xfrm ? this.findFirstByLocalName(xfrm, 'off') : null;
                const ext = xfrm ? this.findFirstByLocalName(xfrm, 'ext') : null;

                if (relId && off && ext) {
                    const mediaFile = await this.resolveImage(relId, slideIndex, zip);
                    if (mediaFile) {
                        elements.push({
                            id: `el-${slideIndex}-${Date.now()}-${zIndex}`,
                            type: 'image',
                            src: URL.createObjectURL(mediaFile),
                            x: this.scaleX(parseInt(this.getAttr(off, 'x') || '0')),
                            y: this.scaleY(parseInt(this.getAttr(off, 'y') || '0')),
                            width: this.scaleX(parseInt(this.getAttr(ext, 'cx') || '0')),
                            height: this.scaleY(parseInt(this.getAttr(ext, 'cy') || '0')),
                            zIndex: zIndex++,
                            opacity: 1
                        });
                    }
                }
            }
        }

        return { id: `slide-${slideIndex}`, elements };
    }
}
